import { RefObject, useEffect, useRef, useState } from 'react';
import { Button, Card, CardHeader, Spinner, Textarea } from '../../../../components/ui';
import { uploadFile, viewUrl } from '../../../../services/api/uploadService';
import { jobService } from '../../services/jobService';
import type { ActionProps } from '../../../../hooks/useAction';
import type { Job } from '../../types/jobTypes';
import type { FormEvent } from 'react';

function Photo({ path }: { path: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    viewUrl(path).then(setUrl).catch(() => setFailed(true));
  }, [path]);
  return (
    <a href={url ?? undefined} target="_blank" rel="noreferrer" className="relative block aspect-square overflow-hidden rounded-lg border border-line bg-navy-50">
      {url ? (
        <img src={url} alt="Site" className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <span className="flex h-full items-center justify-center text-xs text-ink-400">{failed ? 'Unavailable' : <Spinner size={16} />}</span>
      )}
    </a>
  );
}

/** Before / after site photos with upload. */
export function PhotosCard({ job, editable, run, busy, onChange }: ActionProps & { job: Job; editable: boolean; onChange: () => void }) {
  type PhotoKind = 'before' | 'after';
  const inputs: Record<PhotoKind, RefObject<HTMLInputElement>> = { before: useRef<HTMLInputElement>(null), after: useRef<HTMLInputElement>(null) };

  const upload = async (kind: PhotoKind, files: File[]) => {
    if (!files?.length) return;
    const res = await run(`photo-${kind}`, async () => {
      const urls: string[] = [];
      for (const f of files) urls.push(await uploadFile(f, 'service-photos'));
      return jobService.addPhotos(job._id, kind, urls);
    }, `${files.length} photo${files.length > 1 ? 's' : ''} added`);
    if (res) onChange();
  };

  return (
    <Card>
      <CardHeader title="Site photos" />
      <div className="grid gap-5 p-5 pt-4 sm:grid-cols-2">
        {(
          [
            ['before', 'Before work', job.beforePhotos],
            ['after', 'After work', job.afterPhotos],
          ] as [PhotoKind, string, string[] | undefined][]
        ).map(([kind, label, list]) => (
          <div key={kind}>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-medium text-navy-900">{label}</p>
              {editable && (
                <>
                  <Button size="sm" variant="ghost" leftIcon="plus" className="text-brand-600" loading={busy === `photo-${kind}`} onClick={() => inputs[kind].current?.click()}>
                    Add
                  </Button>
                  <input
                    ref={inputs[kind]}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    capture="environment"
                    multiple
                    hidden
                    onChange={(e) => {
                      upload(kind, [...(e.target.files ?? [])]);
                      e.target.value = '';
                    }}
                  />
                </>
              )}
            </div>
            {list?.length ? (
              <div className="grid grid-cols-3 gap-2">{list.map((p) => <Photo key={p} path={p} />)}</div>
            ) : (
              <p className="rounded-lg border border-dashed border-line py-6 text-center text-xs text-ink-400">No photos</p>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Worker and office notes on the job. */
export function NotesCard({ job, run, busy, onChange }: ActionProps & { job: Job; onChange: () => void }) {
  const [text, setText] = useState('');
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (await run('note', () => jobService.addNote(job._id, text.trim()), 'Note added')) {
      setText('');
      onChange();
    }
  };
  const notes = [...(job.workerNotes ?? [])].reverse();
  return (
    <Card>
      <CardHeader title="Site notes" />
      <form onSubmit={add} className="flex flex-col gap-2 p-5 pb-3 pt-4 sm:flex-row sm:items-end">
        <Textarea aria-label="New note" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="What was done, what is pending…" maxLength={2000} containerClassName="flex-1" />
        <Button type="submit" loading={busy === 'note'} disabled={!text.trim()}>Add note</Button>
      </form>
      <ul className="divide-y divide-line">
        {notes.map((n, i) => (
          <li key={`${n.at}-${i}`} className="px-5 py-3">
            <p className="whitespace-pre-line text-sm text-navy-900">{n.text || 'Voice note'}</p>
            <p className="mt-0.5 text-xs text-ink-400">
              {n.by?.name ?? 'Staff'} · {new Date(n.at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}
